import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

import {
  type RequestJoinInvitationBody,
  toInvitationCreateBody,
} from "./types";

type RequestJoinModalProps = {
  idea: OpenAPI.Schemas["Idea"];
  open: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
};

const RequestJoinModal = ({
  idea,
  open,
  onClose,
  onConfirm,
}: RequestJoinModalProps) => {
  const user = useCurrentUser();
  const [note, setNote] = useState("");
  const { t } = useTranslation();

  const handleConfirm = useCallback(() => {
    if (!user?._id) return;
    const body: RequestJoinInvitationBody = {
      forType: "requestJoinIdea",
      forId: idea._id,
      invitee: user._id,
      note,
    };
    api.invitations.create(
      toInvitationCreateBody(body),
      () => {
        toast.success(`Requested to join ${t("generic.idea")} team`);
        onClose();
        onConfirm(note);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [user, note, idea, onClose, onConfirm, t]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader>Request to join {idea.name}</ModalHeader>
      <ModalContent>
        <p>
          Send a message to the {t("generic.idea")} team to request to join the
          team. You will be able to edit the {t("generic.idea")} once you have
          been accepted.
        </p>
        <p>
          Please describe why you would like to join the team, and what you can
          contribute to the {t("generic.idea")} below.
        </p>

        <TextArea
          autoFocus
          value={note}
          placeholder="I would like to join the team because..."
          onChange={(e) => setNote(e.target.value)}
        />
      </ModalContent>
      <ModalActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button onClick={handleConfirm} color="primary">
          Send
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default RequestJoinModal;
